// Read-only recipe view — same visual as the cook's recipe detail page,
// but reused by the nutritionist/admin preview screen (no favorite button).
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Loader2, ChefHat, Eye } from "lucide-react";
import RecipeDisplay from "../../components/RecipeDisplay";

export default function AdminRecipePreview() {
  const { recipe_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [recipe, setRecipe] = useState(null);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [rec, t, c, k] = await Promise.all([
          fetch(`${API}/recipes/${recipe_id}`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
          fetch(`${API}/recipe-tags`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API}/recipe-categories`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API}/kitchens`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
        ]);
        setRecipe(rec);
        setTags(t);
        setCategories(c);
        setKitchens(k);
      } finally {
        setLoading(false);
      }
    })();
  }, [recipe_id]);

  const tagById = Object.fromEntries(tags.map((t) => [t.recipe_tag_id, t]));
  const catById = Object.fromEntries(categories.map((c) => [c.recipe_category_id, c]));
  const kitchenById = Object.fromEntries(kitchens.map((k) => [k.kitchen_id, k]));

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="recipe-preview">
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const back = location.state?.from;
              if (back) navigate(back, { replace: true });
              else navigate("/admin/recipes", { replace: true });
            }}
            className="text-xs"
            data-testid="recipe-preview-back"
          >
            <ArrowLeft size={12} className="mr-1" /> Back
          </Button>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
            <Eye size={11} /> Cook view preview
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400">
            <Loader2 className="animate-spin inline" size={16} />
          </div>
        ) : !recipe ? (
          <div className="p-8 text-center text-gray-400 text-sm">Recipe not found or you don&apos;t have access.</div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-3xl p-5 sm:p-8">
            {/* Mock cook header bar */}
            <div className="bg-white rounded-2xl border border-gray-100 px-4 py-2.5 mb-5 flex items-center justify-between shadow-sm max-w-3xl mx-auto">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                  <ChefHat size={13} />
                </div>
                <span className="font-extrabold text-gray-900 text-xs">Cook</span>
              </div>
              <span className="text-[10px] text-gray-400">As seen by your kitchen team</span>
            </div>

            <div className="max-w-3xl mx-auto">
              <RecipeDisplay
                recipe={recipe}
                category={catById[recipe.recipe_category_id]}
                tagsById={tagById}
                kitchensById={kitchenById}
                linkBasePath="/admin/recipes"
                linkPathSuffix="/preview"
                currentPath={location.pathname}
              />
            </div>

            <div className="mt-5 text-center">
              <Link to={`/admin/recipes?edit=${recipe.recipe_id}`} className="text-xs text-emerald-700 hover:underline font-semibold" data-testid="recipe-preview-edit-link">
                ← Back to editor
              </Link>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
